/**
 * One way to upload a file, for every upload in the app.
 *
 * 🔑 WHY THIS EXISTS. Every upload used to POST multipart to a Pages Function,
 * which buffered the whole file in a 128 MB isolate that all our /api routes
 * share. On 2026-08-27/28 that exhausted the isolate and Cloudflare answered
 * Error 1102 — to unrelated requests: a doctor opening a patient chart got an
 * error page because someone else was uploading an X-ray. The fix is to send the
 * bytes browser → Wasabi and never through the Worker. functions/lib/presign.js
 * is the server half.
 *
 * THE SHAPE
 *   1. POST `${endpoint}?presign=1` with JSON metadata → { url, headers, result }.
 *      The server derives the object key (never us — see keyFor in upload.js) and
 *      signs a PUT for exactly that key.
 *   2. PUT the file straight at `url` with `headers` VERBATIM. The Content-Type is
 *      part of the signature; changing it invalidates the URL.
 *   3. Return `result` — the same object the old multipart route returned, so the
 *      callers' record-building code is unchanged.
 *
 * …AND THE SHAPE FOR ROUTES THAT WRITE THE RECORD THEMSELVES (`opts.attach`)
 * Some routes cannot hand step 3 to us. /api/me/files stamps `created_at` and
 * `user_id` on its `file[]` entry because those two open a 24-hour delete window
 * (canDeleteEntry in functions/api/me/_me.js) — a browser that could set them
 * would hold its own window open. Those routes answer the presign WITHOUT a
 * `result` and expose a second call instead:
 *   3b. POST `${endpoint}?attach=1` with { …fields, name, type, size, location }
 *       → the server re-validates the key, HEADs the object, writes the Xano
 *         record and returns exactly what its multipart path returns.
 * Pass `{ attach: true }` to take that road, or `{ attach: {…} }` to send extra
 * fields with the attach call only — an inline thumbnail belongs there, not in
 * the presign round trip. The contract is written up at the top of
 * functions/api/me/files.js.
 *
 * ⚠️ THE FALLBACK IS NOT OPTIONAL. A direct PUT only works once the Wasabi bucket
 * has a CORS rule allowing PUT from this origin (docs/wasabi-cors.md). Until that
 * is set on a given bucket the PUT fails in the browser, so we fall back to the
 * old multipart POST and the upload still succeeds — slower, and back on the path
 * that caused the outage, but never broken for the user. Once a bucket's CORS is
 * in place its uploads silently start taking the direct path. Nothing to redeploy.
 *
 * A refusal from OUR server (413 too large, 403 not your role) is NOT a fallback
 * case — it is the answer, and retrying it as multipart would just buffer a file
 * we already know is going to be rejected.
 */
import { redirectToLogin } from './api.js'
import { isTraining, TRAINING_FILES_OFF_TH } from './training.js'

// Endpoints whose direct PUT we have already seen fail — almost always a bucket
// without CORS. Remembered for the tab's lifetime so we pay that failed round
// trip once, not once per file. Cleared by a reload, which is what you want after
// someone fixes the bucket policy.
const directBroken = new Set()

/** Did the server answer with a deliberate refusal we must show the user? */
const isRefusal = (status) => status === 400 || status === 403 || status === 413 || status === 415

/**
 * Upload one file.
 *
 * @param endpoint  '/api/upload', '/api/asset-upload', … — the same route as before.
 * @param fields    the non-file fields the route needs ({ hn, kind }, { asset_id }, …).
 * @param file      a File or Blob. A Blob must be given a name via `fields.name`.
 * @param opts      optional. `{ attach: true }` for a route that writes the Xano
 *                  record itself (see the block above); `{ attach: { … } }` to add
 *                  fields to the attach call only. Omit it and nothing changes.
 * @returns         whatever the multipart route returned — { ok, location, url, … }.
 */
export async function uploadFile(endpoint, fields, file, opts) {
  // Training sandbox: refused before any request (src/lib/training.js). AuthGate's guard
  // stops the file pickers; this catches the uploads no picker starts (a canvas blob, an
  // edited image saved back). `refused` so no caller retries it through the proxy.
  if (isTraining()) {
    const e = new Error(TRAINING_FILES_OFF_TH)
    e.refused = true
    throw e
  }
  const name = file?.name || fields?.name || 'untitled'
  const type = file?.type || 'application/octet-stream'
  const size = file?.size || 0
  // Fields that only the record write needs. Kept out of the presign body so an
  // inline thumbnail is not sent twice — and out of the isolate on the road we
  // actually want everyone taking.
  const attachOnly = (opts?.attach && typeof opts.attach === 'object') ? opts.attach : {}

  if (!directBroken.has(endpoint)) {
    try {
      return await viaPresign(endpoint, { ...fields, name, type, size }, file, opts?.attach ? attachOnly : null)
    } catch (e) {
      if (e?.refused) throw e
      // The presign itself worked; the browser could not reach Wasabi. That is a
      // bucket CORS gap, not a user error — say so once, then take the slow road.
      // `directOk` marks a failure that happened AFTER a good PUT: blaming CORS
      // then would send every later upload back through the Worker for nothing.
      if (!e?.directOk) directBroken.add(endpoint)
      console.warn(`[upload] direct PUT unavailable for ${endpoint} (bucket CORS?) — falling back to the proxy:`, e?.message || e)
    }
  }
  return viaMultipart(endpoint, { ...fields, ...attachOnly }, file, name)
}

async function viaPresign(endpoint, meta, file, attachFields) {
  const r = await fetch(`${endpoint}?presign=1`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(meta),
  })
  if (r.status === 401) { redirectToLogin(); throw await refusal(r, 'unauthorized') }
  if (!r.ok) {
    // 404/405 = this route has no presign mode. Not a refusal — fall back.
    if (!isRefusal(r.status)) throw new Error(`presign HTTP ${r.status}`)
    throw await refusal(r)
  }
  const signed = await r.json()
  if (!signed?.url) throw new Error('presign returned no url')

  // `headers` verbatim — Content-Type is signed into the URL.
  const put = await fetch(signed.url, { method: 'PUT', body: file, headers: signed.headers })
  if (!put.ok) throw new Error(`wasabi PUT HTTP ${put.status}`)
  if (!attachFields) return signed.result

  return viaAttach(endpoint, { ...meta, ...attachFields, location: signed.location })
}

/**
 * Hand the landed object to the server so IT writes the record.
 *
 * ⚠️ NOTHING HERE FALLS BACK. The bytes are already in the bucket, so re-running
 * the multipart path would upload them a second time — through the isolate whose
 * memory this whole file exists to protect — to reach the same Xano that just
 * refused. Every failure is therefore the answer, and it is marked `directOk` so
 * the caller does not blacklist the direct path over a Xano problem.
 */
async function viaAttach(endpoint, body) {
  let r
  try {
    r = await fetch(`${endpoint}?attach=1`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    })
  } catch (e) {
    const err = new Error(`บันทึกไฟล์ไม่สำเร็จ — ${e?.message || e}`)
    err.refused = true
    err.directOk = true
    throw err
  }
  if (r.status === 401) { redirectToLogin(); throw await attachRefusal(r, 'unauthorized') }
  if (!r.ok) throw await attachRefusal(r)
  try {
    return await r.json()
  } catch {
    const err = new Error('บันทึกไฟล์ไม่สำเร็จ — คำตอบจากเซิร์ฟเวอร์ไม่ถูกต้อง')
    err.refused = true
    err.directOk = true
    throw err
  }
}

async function attachRefusal(res, fallbackMessage) {
  const e = await refusal(res, fallbackMessage)
  e.directOk = true
  return e
}

async function viaMultipart(endpoint, fields, file, name) {
  const fd = new FormData()
  // FormData wants a File-like with a name; a raw Blob (a generated thumbnail)
  // would otherwise arrive as "blob" and lose its extension.
  fd.append('file', file instanceof File ? file : new File([file], name, { type: file?.type || 'application/octet-stream' }))
  for (const [k, v] of Object.entries(fields || {})) {
    if (v !== undefined && v !== null) fd.append(k, String(v))
  }
  const r = await fetch(endpoint, { method: 'POST', body: fd, credentials: 'include' })
  if (r.status === 401) { redirectToLogin(); throw await refusal(r, 'unauthorized') }
  if (!r.ok) throw await refusal(r)
  return r.json()
}

/**
 * Turn a failed response into an Error that still carries everything a caller
 * needs to explain itself.
 *
 * ⚠️ `.body` IS THE POINT. The upload routes answer with JSON that already names
 * the cause — `{ error: 'wasabi_forbidden', bucket, detail: '<XML …>' }` — and
 * PurchaseFiles turns exactly those fields into a Thai sentence a stock manager
 * can act on ("บัญชีของแอปยังไม่มีสิทธิ์เขียนลงที่เก็บไฟล์ …"). Collapsing all of
 * that into `message` is what once turned a bucket-permission problem into a
 * mystery "(502)", so the parsed body rides along and callers keep the choice.
 */
async function refusal(res, fallbackMessage) {
  const text = await res.text().catch(() => '')
  let body = null
  try { body = JSON.parse(text) } catch { /* plain text or empty */ }
  const e = new Error(fallbackMessage || body?.hint || body?.detail || `อัปโหลดล้มเหลว: HTTP ${res.status} · ${text || res.statusText}`)
  e.refused = true
  e.status = res.status
  e.body = body
  return e
}
